import * as React from 'react';
import { cn } from '../../../utils';
import { zineZeroPad } from './zine-theme';

export interface ZineAgendaItem {
  /** item number; numbers are zero-padded ("01") */
  no: number | string;
  title: React.ReactNode;
  /** e.g. "Read · how agreement disarms disagreement" */
  subtitle?: React.ReactNode;
  /** kind chip, e.g. "Read" / "Listen" / "Practice" / "Speak" */
  kind?: React.ReactNode;
  /** right cell when not done, e.g. "7 min" */
  minutes?: React.ReactNode;
  /** when true the right cell shows `doneLabel` in gold */
  done?: boolean;
  /** gold done text, e.g. "✓ done" */
  doneLabel?: React.ReactNode;
  /** link target; renders an <a>. Beware: the p2e app has `<base href="/">`, so a bare "#s1" resolves to the home page — use `onClick` to scroll instead */
  href?: string;
  /** click handler; without `href` the row renders as a <button> */
  onClick?: React.MouseEventHandler<HTMLElement>;
}

export interface AgendaBandProps {
  /** band label, e.g. "In this class" (pink, with trailing rule) */
  label: React.ReactNode;
  items: ZineAgendaItem[];
  /** accessible name for the <nav> landmark */
  ariaLabel?: string;
  className?: string;
}

/**
 * Dark "In this class" contents band: pink label with rule, numbered list
 * of blocks with kind chip and minutes / gold "done" cell.
 */
export function AgendaBand({ label, items, ariaLabel, className }: AgendaBandProps) {
  return (
    <nav className={cn('z-agenda-band', className)} aria-label={ariaLabel}>
      <div className="z-page">
        <p className="z-band-label">{label}</p>
        <ul className="z-contents-list">
          {items.map((item, i) => {
            const cells = (
              <>
                <span className="z-no">{zineZeroPad(item.no)}</span>
                <span>
                  <span className="z-t">{item.title}</span>
                  {item.subtitle != null && <small>{item.subtitle}</small>}
                </span>
                {item.kind != null && <span className="z-kind">{item.kind}</span>}
                {item.done ? (
                  <span className="z-mins z-done-dot">
                    {item.doneLabel ?? '✓ done'}
                  </span>
                ) : (
                  <span className="z-mins">{item.minutes}</span>
                )}
              </>
            );
            return (
              <li key={i}>
                {item.href != null ? (
                  <a
                    className="z-contents-row"
                    href={item.href}
                    onClick={item.onClick}
                  >
                    {cells}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="z-contents-row"
                    onClick={item.onClick}
                  >
                    {cells}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
